import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import mishLogo from '../assets/mish.png';
import { solidGlassOnMobile } from '../styles/performance';
const NavbarContainer = styled.nav`
  position: fixed;
  top: 0; left: 0; right: 0;
  display: flex; justify-content: space-between; align-items: center;
  padding: 1rem 4rem;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  ${solidGlassOnMobile}
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  z-index: 1000;
  transition: all 0.3s ease-in-out;

  &.scrolled {
    padding: 0.8rem 4rem;
    background: rgba(0, 0, 0, 0.95);
    box-shadow: 0 4px 30px rgba(0, 0, 0, 0.3);
  }

  @media (max-width: 768px) {
    padding: 1rem 2rem;
    &.scrolled { padding: 0.8rem 2rem; }
  }
`;

const Logo = styled(Link)`
  display: flex; align-items: center; padding: 0.5rem 0; position: relative;
  text-decoration: none;

  &::after {
    content: ''; position: absolute; top: 50%; left: 50%;
    transform: translate(-50%, -50%); width: 120%; height: 120%;
    background: radial-gradient(circle, rgba(179,136,255,0.1) 0%, transparent 70%);
    opacity: 0; transition: opacity 0.3s ease;
  }
  &:hover::after { opacity: 1; }
`;

const LogoImage = styled.img`
  height: 75px; width: auto;
  transition: all 0.5s cubic-bezier(0.4,0,0.2,1);
  filter: brightness(0) invert(1); opacity: 0.95;

  .scrolled & { height: 65px; }
  &:hover { opacity: 1; filter: brightness(0) invert(1) drop-shadow(0 0 15px rgba(179,136,255,0.7)); }

  @media (max-width: 1024px) {
    height: 60px; .scrolled & { height: 50px; }
  }
  @media (max-width: 768px) {
    height: 50px; .scrolled & { height: 45px; }
  }
`;

const NavLinks = styled.div`
  display: flex; align-items: center; gap: 2.5rem;
  @media (max-width: 768px) { display: none; }
`;

const NavLink = styled(Link)`
  color: white; text-decoration: none; font-size: 1rem; font-weight: 500;
  position: relative; padding: 0.5rem 0; transition: all 0.3s ease;

  &::after {
    content: ''; position: absolute; bottom: 0; left: 0; width: 0; height: 2px;
    background: linear-gradient(90deg, #b388ff, transparent); transition: width 0.3s ease;
  }
  &:hover { color: #b388ff; &::after { width: 100%; } }
  &.active { color: #b388ff; &::after { width: 100%; } }
`;

const MobileMenuButton = styled.button`
  display: none; background: none; border: none; color: white;
  font-size: 1.6rem; cursor: pointer; padding: 0.5rem; transition: all 0.3s ease;
  @media (max-width: 768px) { display: block; }
  &:hover { color: #b388ff; }
`;

const MobileBackdrop = styled.div`
  position: fixed; inset: 0; z-index: 999;
  background: rgba(0,0,0,0.5);
  backdrop-filter: blur(2px); -webkit-backdrop-filter: blur(2px);
  opacity: ${p => (p.$open ? 1 : 0)};
  pointer-events: ${p => (p.$open ? 'auto' : 'none')};
  transition: opacity .25s ease;
  @media (min-width: 769px) { display: none; }
`;

const MobilePanel = styled.div`
  position: fixed; top: 0; left: 0; right: 0; z-index: 1000;
  background: rgba(0,0,0,0.95);
  border-bottom: 1px solid rgba(255,255,255,0.1);
  transform: translateY(${p => (p.$open ? '0' : '-100%')});
  transition: transform .28s ease;
  padding: 72px 1.5rem 1.25rem;  /* account for navbar height */
  @media (min-width: 769px) { display: none; }
`;

const MobileLinks = styled.div`
  display: grid; gap: 1rem;
`;

const MobileLink = styled(Link)`
  color: white; text-decoration: none; font-size: 1.05rem; font-weight: 600;
  padding: 0.75rem 0; border-bottom: 1px solid rgba(255,255,255,0.06);
  &:last-child { border-bottom: none; }
  &.active { color: #b388ff; }
`;

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeLink, setActiveLink] = useState('home');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        setIsScrolled(window.scrollY > 50);
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (location.pathname === '/') setActiveLink('home');
    else if (location.pathname === '/about-us') setActiveLink('about');
    else if (location.pathname === '/contact-us') setActiveLink('contact');
    else if (location.pathname === '/careers') setActiveLink('careers');
  }, [location]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMenuOpen]);

  const handleScroll = (e, targetId) => {
    if (location.pathname !== '/') {
      window.location.href = '/#' + targetId;
      return;
    }
    if (e) e.preventDefault();
    setActiveLink(targetId);
    setIsMenuOpen(false);
    const el = document.getElementById(targetId);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <NavbarContainer className={isScrolled ? 'scrolled' : ''}>
      <Logo to="/" onClick={(e) => handleScroll(e, 'home')}>
        <LogoImage src={mishLogo} alt="MISHRA WARNA" />
      </Logo>

      {/* Desktop links */}
      <NavLinks>
        <NavLink to="/" className={activeLink === 'home' ? 'active' : ''} onClick={(e) => handleScroll(e, 'home')}>
          Home
        </NavLink>
        <NavLink to="/" className={activeLink === 'ecosystems' ? 'active' : ''} onClick={(e) => handleScroll(e, 'ecosystems')}>
          Projects
        </NavLink>
        <NavLink to="/" className={activeLink === 'contact' ? 'active' : ''} onClick={(e) => handleScroll(e, 'contact')}>
          Contact
        </NavLink>
        <NavLink to="/" className={activeLink === 'about' ? 'active' : ''} onClick={(e) => handleScroll(e, 'about')}>
          About US
        </NavLink>
        <NavLink to="/careers" className={activeLink === 'careers' ? 'active' : ''} onClick={() => { setActiveLink('careers'); setIsMenuOpen(false); }}>
          Careers
        </NavLink>
      </NavLinks>

      {/* Mobile toggle */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <MobileMenuButton
          aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={isMenuOpen}
          onClick={() => setIsMenuOpen(v => !v)}
        >
          {isMenuOpen ? '✕' : '☰'}
        </MobileMenuButton>
      </div>

      {/* Mobile overlay */}
      <MobileBackdrop $open={isMenuOpen} onClick={() => setIsMenuOpen(false)} />
      <MobilePanel $open={isMenuOpen}>
        <MobileLinks>
          <MobileLink to="/" className={activeLink === 'home' ? 'active' : ''} onClick={(e) => handleScroll(e, 'home')}>
            Home
          </MobileLink>
          <MobileLink to="/" className={activeLink === 'ecosystems' ? 'active' : ''} onClick={(e) => handleScroll(e, 'ecosystems')}>
            Projects
          </MobileLink>
          <MobileLink to="/" className={activeLink === 'contact' ? 'active' : ''} onClick={(e) => handleScroll(e, 'contact')}>
            Contact
          </MobileLink>
          <MobileLink to="/" className={activeLink === 'about' ? 'active' : ''} onClick={(e) => handleScroll(e, 'about')}>
            About US
          </MobileLink>
          <MobileLink to="/careers" className={activeLink === 'careers' ? 'active' : ''} onClick={() => { setActiveLink('careers'); setIsMenuOpen(false); }}>
            Careers
          </MobileLink>
        </MobileLinks>
      </MobilePanel>
    </NavbarContainer>
  );
};

export default Navbar;
